import React from 'react';

import {ArchiveOptions, ItemForArchive} from 'archive/ArchiveConstants';
import ArchiveTableRowName from 'archive/components/modals/ArchiveTableRowName';
import {ArchiveTableRowType} from 'archive/components/modals/ArchiveTableRowType';
import isBatchesEnabled from 'registry/IsBatchesEnabled';
import CheckableSortableTable from 'ui-library/CheckableSortableTable';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import APIUtil from 'util/APIUtil';

interface ArchiveTableProps {
  itemsForArchive: Array<ItemForArchive>;
  options: ArchiveOptions;
}

function getColumns(options: ArchiveOptions): Array<SortableTableColumn> {
  return [
    SortableTable.defaultColumn('Type', 'id', {
      render: (column, row: ItemForArchive) => <ArchiveTableRowType itemForArchive={row} options={options} />,
    }),
    SortableTable.defaultColumn('Name', 'name', {
      render: (column, row: ItemForArchive) => (
        <ArchiveTableRowName
          itemForArchive={row}
          options={options}
          dataTestAttrs={{component: 'ArchiveTable', element: 'rowName'}}
        />
      ),
    }),
  ];
}

export default function ArchiveTable({itemsForArchive, options}: ArchiveTableProps): JSX.Element {
  function filterItemsForDisplay(items: Array<ItemForArchive>): Array<ItemForArchive> {
    return items.filter(
      (item) =>
        !item.isProjectRootFolder &&
        (isBatchesEnabled() || !APIUtil.isBatch(item.id)) &&
        !APIUtil.isProcessSchema(item.id)
    );
  }

  return (
    <div className="archiveModal-table">
      <SortableTable
        columns={getColumns(options)}
        rows={filterItemsForDisplay(itemsForArchive)}
        clickable={false}
        selectable={false}
        hoverable={true}
        coloredHeader={true}
        wrapCell={true}
      />
    </div>
  );
}

interface CheckableArchiveTableProps extends ArchiveTableProps {
  checkedRowIds: Array<string>;
  onChangeChecks: (newCheckedRowIds: Array<string>) => void;
}

export function CheckableArchiveTable(props: CheckableArchiveTableProps): JSX.Element {
  const checkedRowIdSet: Set<string | number> = new Set(props.checkedRowIds);
  return (
    <div className="archiveModal-table">
      <CheckableSortableTable
        columns={getColumns(props.options)}
        rows={props.itemsForArchive.map((item) => ({...item, checked: checkedRowIdSet.has(item.id)}))}
        clickable={false}
        selectable={false}
        hoverable={true}
        coloredHeader={true}
        wrapCell={true}
        onChangeChecks={props.onChangeChecks}
      />
    </div>
  );
}
